import { memo } from 'react';
import clsx from 'clsx';
import {
    GlowChip,
    GlowClickable,
    GlowIcon,
    GlowSkeleton,
    GlowTextBody,
    PropsWithAriaLabel,
    PropsWithClassName,
    PropsWithCTA,
    PropsWithIcon,
    PropsWithRedDot,
    PropsWithSkeleton,
} from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { DataLayerClassName } from '../../dataLayer';

type ConnectionBannerItemProps = PropsWithCTA &
    PropsWithClassName &
    PropsWithAriaLabel &
    PropsWithSkeleton &
    PropsWithRedDot &
    PropsWithIcon & {
        label: string;
        isObfuscated?: boolean;
        colorClassName?: string;
        disabled?: boolean;
        isRedirecting?: boolean;
    };

export const ConnectionBannerItem: ComponentMemo<ConnectionBannerItemProps> = memo(
    ({
        className,
        label,
        Icon,
        ariaLabel,
        isObfuscated = false,
        isRedirecting = false,
        isRedDot = false,
        colorClassName,
        disabled,
        skeleton,
        ...props
    }) => {
        return (
            <GlowClickable
                className={clsx(
                    'fill-text-primary text-text-primary',
                    'gap-1 hover:text-underline',
                    DataLayerClassName.connectionBanner,
                    className,
                )}
                ariaLabel={ariaLabel}
                content={{
                    labelElement: isRedirecting ? (
                        <GlowSkeleton
                            height={skeleton?.height}
                            width={skeleton?.width}
                            itemClassName={skeleton?.itemClassName}
                        />
                    ) : (
                        <GlowTextBody text={label} className={clsx(colorClassName, 'text-nowrap')} TagName={'span'} />
                    ),
                    icon: !isRedirecting && (
                        <div className={'relative'}>
                            <GlowIcon className={colorClassName} Icon={Icon} type={'large'} />
                            {isRedDot && (
                                <GlowChip
                                    hasDigit={false}
                                    className={
                                        'bg-surface-promotion min-w-iconChipMedium min-h-iconChipMedium max-w-iconChipMedium max-h-iconChipMedium absolute -top-0.375 -right-0.375'
                                    }
                                    size={'medium'}
                                />
                            )}
                        </div>
                    ),
                }}
                isObfuscated={isObfuscated}
                disabled={disabled}
                {...props}
            />
        );
    },
);

ConnectionBannerItem.displayName = 'ConnectionBannerItem';
